import React, { useState, useMemo } from 'react';
import {
  ChevronRight,
  Sparkles,
  MapPin,
  Search,
  X,
  Compass,
  Navigation,
  ShieldCheck,
  CheckCircle2,
  LayoutGrid,
  Map as MapIcon,
} from 'lucide-react';
import { Companion, SearchFilterState } from '../types';
import { CompanionCard } from './CompanionCard';
import { DynamicLocationSearchBox } from './location/DynamicLocationSearchBox';
import { RadiusFilterSelector } from './location/RadiusFilterSelector';
import { InteractiveGoogleMap } from './location/InteractiveGoogleMap';
import { calculateDistanceKm } from '../lib/locationService';
import { getCoordinatesForCity } from '../data/companionsData';
import { StructuredLocation, SearchRadiusKm } from '../types/location';
import { isCompanionAvailableForService } from '../utils/companionServiceMatcher';

interface PopularCompanionsSectionProps {
  companions: Companion[];
  filters: SearchFilterState;
  setFilters: React.Dispatch<React.SetStateAction<SearchFilterState>>;
  wishlist: string[];
  onToggleWishlist: (id: string, e: React.MouseEvent) => void;
  onSelectCompanion: (companion: Companion) => void;
  onBookNow: (companion: Companion, e: React.MouseEvent) => void;
}

export const PopularCompanionsSection: React.FC<PopularCompanionsSectionProps> = ({
  companions,
  filters,
  setFilters,
  wishlist,
  onToggleWishlist,
  onSelectCompanion,
  onBookNow,
}) => {
  const [activeCategory, setActiveCategory] = useState<'all' | 'male' | 'female' | 'top-rated' | 'budget'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'map'>('grid');

  const currentRadius: SearchRadiusKm = filters.radiusKm || 25;
  const currentCityFilter = filters.city || 'all';
  const hasCoordinatesSearch = Boolean(filters.location?.latitude && filters.location?.longitude);

  // Compute distance for each companion and filter by radius & criteria
  const { filteredCompanions, totalWithinIndia } = useMemo(() => {
    let list = companions.map((comp) => {
      let distanceKm: number | undefined = undefined;

      if (filters.location?.latitude && filters.location?.longitude) {
        const cCoord = getCoordinatesForCity(comp.city);
        const cLat = comp.latitude || cCoord.latitude;
        const cLng = comp.longitude || cCoord.longitude;

        distanceKm = calculateDistanceKm(
          filters.location.latitude,
          filters.location.longitude,
          cLat,
          cLng
        );
      }

      return {
        ...comp,
        _distanceKm: distanceKm,
      };
    });

    // 1. Gender category filter
    if (activeCategory === 'male') list = list.filter((c) => c.gender === 'male');
    if (activeCategory === 'female') list = list.filter((c) => c.gender === 'female');
    if (activeCategory === 'top-rated') list = list.filter((c) => c.rating >= 4.8);
    if (activeCategory === 'budget') list = list.filter((c) => c.hourlyRate <= 1000);

    if (filters.gender !== 'all') {
      list = list.filter((c) => c.gender === filters.gender);
    }

    // 2. Service filter
    if (filters.service && filters.service !== 'all' && filters.service !== 'All Occasions') {
      list = list.filter((c) => isCompanionAvailableForService(c, filters.service!));
    }

    // 3. Search query filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.city.toLowerCase().includes(q) ||
          (c.state || '').toLowerCase().includes(q) ||
          c.bio.toLowerCase().includes(q) ||
          c.about.toLowerCase().includes(q) ||
          c.supportedOccasions.some((o) => o.toLowerCase().includes(q)) ||
          c.interests.some((i) => i.toLowerCase().includes(q))
      );
    }

    const totalIndia = list.length;

    // 4. Dynamic Location Radius / Text filter
    if (hasCoordinatesSearch && filters.location) {
      list = list.filter(
        (c) => c._distanceKm !== undefined && c._distanceKm <= currentRadius
      );
      // Sort by closest distance ascending
      list.sort((a, b) => (a._distanceKm || 0) - (b._distanceKm || 0));
    } else if (currentCityFilter !== 'all') {
      const qCity = currentCityFilter.toLowerCase().trim();
      list = list.filter((c) => {
        const compCity = c.city.toLowerCase();
        const compState = (c.state || '').toLowerCase();
        const matchCity = compCity.includes(qCity) || qCity.includes(compCity);
        const matchState = compState.includes(qCity);
        const matchCoverage = c.coverageCities?.some(
          (cov) => cov.toLowerCase().includes(qCity) || qCity.includes(cov.toLowerCase())
        );
        return matchCity || matchState || matchCoverage;
      });
    }

    return { filteredCompanions: list, totalWithinIndia: totalIndia };
  }, [
    companions,
    activeCategory,
    filters.gender,
    filters.service,
    filters.location,
    filters.radiusKm,
    currentCityFilter,
    currentRadius,
    hasCoordinatesSearch,
    searchQuery,
  ]);

  const handleLocationSelect = (loc: StructuredLocation | null) => {
    if (loc) {
      setFilters((prev) => ({
        ...prev,
        city: loc.city || loc.formatted_address,
        location: loc,
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        city: 'all',
        location: null,
      }));
    }
  };

  const handleRadiusChange = (radius: SearchRadiusKm) => {
    setFilters((prev) => ({ ...prev, radiusKm: radius }));
  };

  const handleClearLocation = () => {
    setFilters((prev) => ({ ...prev, city: 'all', location: null }));
  };

  return (
    <section id="companions-section" className="py-14 sm:py-16 bg-slate-50/70 border-t border-b border-pink-100/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header with Title and Reset */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-extrabold bg-pink-100 text-pink-700 uppercase tracking-wider">
                100% Identity Verified Profiles
              </span>
              <span className="text-xs text-slate-400">• Real-time Google Maps Discovery</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
              Verified Social Companions Near You
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 mt-1 max-w-2xl">
              Search any city, locality, town, landmark, or PIN code in India. Instant distance calculation with radius filtering.
            </p>
          </div>

          {/* Gender Filter Tabs & View All */}
          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="inline-flex p-1 bg-white rounded-2xl border border-slate-200 shadow-2xs">
              <button
                id="filter-cat-all"
                onClick={() => {
                  setActiveCategory('all');
                  setFilters((prev) => ({ ...prev, gender: 'all' }));
                }}
                className={`px-3.5 py-1.5 text-xs font-bold rounded-xl transition-all cursor-pointer ${
                  activeCategory === 'all' && filters.gender === 'all'
                    ? 'bg-pink-600 text-white shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                All
              </button>
              <button
                id="filter-cat-girlfriends"
                onClick={() => {
                  setActiveCategory('female');
                  setFilters((prev) => ({ ...prev, gender: 'female' }));
                }}
                className={`px-3.5 py-1.5 text-xs font-bold rounded-xl transition-all cursor-pointer ${
                  activeCategory === 'female' || filters.gender === 'female'
                    ? 'bg-pink-600 text-white shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Girlfriends
              </button>
              <button
                id="filter-cat-boyfriends"
                onClick={() => {
                  setActiveCategory('male');
                  setFilters((prev) => ({ ...prev, gender: 'male' }));
                }}
                className={`px-3.5 py-1.5 text-xs font-bold rounded-xl transition-all cursor-pointer ${
                  activeCategory === 'male' || filters.gender === 'male'
                    ? 'bg-purple-600 text-white shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Boyfriends
              </button>
            </div>

            <button
              id="btn-view-all-companions"
              onClick={() => {
                setActiveCategory('all');
                setFilters({ gender: 'all', city: 'all', location: null, radiusKm: 25, date: '', time: '', service: undefined });
                setSearchQuery('');
              }}
              className="inline-flex items-center gap-1 px-3.5 py-2 text-xs font-bold text-pink-600 bg-pink-50 hover:bg-pink-100 border border-pink-200 rounded-2xl transition-all cursor-pointer"
            >
              <span>Reset Filters</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>

            {/* View Switcher: Grid vs Interactive Map */}
            <div className="inline-flex p-1 bg-white rounded-2xl border border-slate-200 shadow-2xs">
              <button
                type="button"
                id="btn-view-grid"
                onClick={() => setViewMode('grid')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-xl transition-all cursor-pointer ${
                  viewMode === 'grid'
                    ? 'bg-slate-900 text-white shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Grid View"
              >
                <LayoutGrid className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Grid</span>
              </button>
              <button
                type="button"
                id="btn-view-map"
                onClick={() => setViewMode('map')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-xl transition-all cursor-pointer ${
                  viewMode === 'map'
                    ? 'bg-pink-600 text-white shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Interactive Map View"
              >
                <MapIcon className="w-3.5 h-3.5" />
                <span>Map ({filteredCompanions.length})</span>
              </button>
            </div>
          </div>
        </div>

        {/* Active Service Filter Banner if selected */}
        {filters.service && filters.service !== 'all' && (
          <div className="mb-4 bg-gradient-to-r from-pink-50 to-rose-50 border border-pink-200 rounded-2xl px-4 py-2.5 flex items-center justify-between gap-2 shadow-2xs">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-pink-500 animate-ping" />
              <span className="text-xs font-bold text-slate-800">
                Showing available companions offering:
              </span>
              <span className="px-2.5 py-0.5 bg-pink-600 text-white text-xs font-extrabold rounded-full shadow-2xs">
                {filters.service}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setFilters((prev) => ({ ...prev, service: undefined }))}
              className="text-xs font-semibold text-slate-500 hover:text-red-600 flex items-center gap-1 cursor-pointer"
            >
              <X className="w-3.5 h-3.5" />
              <span>Clear Filter</span>
            </button>
          </div>
        )}

        {/* Dynamic Location & Search Controls Card */}
        <div className="mb-6 bg-white rounded-3xl border border-slate-200/90 shadow-sm p-4 sm:p-5 space-y-3.5">
          {/* Top Row: Search Input + Dynamic Google Places Location Search Box + Result Count */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
            
            {/* Search Input for companion name / hobby / keyword */}
            <div className="md:col-span-6 relative">
              <input
                id="companions-inline-search"
                type="text"
                placeholder="Search companion name, college, hobby (e.g. Priyo, Kathak, wedding)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-8 py-2.5 text-xs sm:text-sm text-slate-900 placeholder:text-slate-400 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-hidden focus:ring-2 focus:ring-pink-500/30 focus:border-pink-500 font-medium"
              />
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3 pointer-events-none" />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-2.5 p-1 rounded-full text-slate-400 hover:text-slate-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* IQ200 Dynamic Google Places Autocomplete (Strict India restriction) */}
            <div className="md:col-span-4">
              <DynamicLocationSearchBox
                placeholder="📍 Search your city, area or location"
                selectedLocation={filters.location}
                onLocationSelect={handleLocationSelect}
                size="md"
              />
            </div>

            {/* Result Counter Badge */}
            <div className="md:col-span-2 flex items-center justify-end">
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-pink-50 rounded-xl border border-pink-100 text-xs font-bold text-pink-700">
                <Sparkles className="w-3.5 h-3.5 text-pink-600" />
                <span>{filteredCompanions.length} Available</span>
              </div>
            </div>
          </div>

          {/* IQ200 PostGIS Radius Filter Bar */}
          <div className="pt-2 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Compass className="w-4 h-4 text-pink-600" />
              <span className="text-xs font-bold text-slate-700">
                Discovery Radius:
              </span>
              <RadiusFilterSelector
                selectedRadius={currentRadius}
                onRadiusChange={handleRadiusChange}
                disabled={!hasCoordinatesSearch && currentCityFilter === 'all'}
              />
            </div>

            {/* Active Location Info & Clear Button */}
            {(filters.location || currentCityFilter !== 'all') && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-slate-600 flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5 text-pink-500" />
                  <span>
                    Searching near <strong className="text-pink-700">{filters.location?.formatted_address || currentCityFilter}</strong> (within {currentRadius} km)
                  </span>
                </span>
                <button
                  type="button"
                  onClick={handleClearLocation}
                  className="px-2 py-0.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-[11px] transition-colors cursor-pointer"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Companions Display (Grid View or Interactive Google Map View) */}
        {filteredCompanions.length > 0 ? (
          viewMode === 'map' ? (
            <div className="space-y-3">
              <InteractiveGoogleMap
                companions={filteredCompanions}
                centerLocation={filters.location}
                onSelectCompanion={onSelectCompanion}
                onBookNow={onBookNow}
                height="500px"
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filteredCompanions.map((comp) => (
                <CompanionCard
                  key={comp.id}
                  companion={comp}
                  isWishlisted={wishlist.includes(comp.id)}
                  onToggleWishlist={onToggleWishlist}
                  onSelect={onSelectCompanion}
                  onBookNow={onBookNow}
                  distanceKm={comp._distanceKm}
                />
              ))}
            </div>
          )
        ) : (
          /* Empty state with Radius expansion prompts */
          <div className="text-center py-16 bg-white rounded-3xl border border-slate-200 p-8 max-w-lg mx-auto shadow-xs">
            <div className="w-14 h-14 rounded-full bg-pink-50 text-pink-500 flex items-center justify-center mx-auto mb-4 text-2xl">
              📍
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-1">
              No Companions Found Within {currentRadius} km
            </h3>
            <p className="text-xs sm:text-sm text-slate-500 mb-5 leading-relaxed">
              No companion is currently within {currentRadius} km of <strong className="text-slate-700">{filters.location?.formatted_address || currentCityFilter}</strong>. Expand your search radius or view companions across all of India!
            </p>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {currentRadius < 50 && (
                <button
                  type="button"
                  onClick={() => handleRadiusChange(currentRadius === 5 ? 10 : currentRadius === 10 ? 25 : 50)}
                  className="px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white text-xs font-bold rounded-xl shadow-xs transition-colors cursor-pointer"
                >
                  Expand Radius to {currentRadius === 5 ? '10 km' : currentRadius === 10 ? '25 km' : '50 km'}
                </button>
              )}
              <button
                type="button"
                onClick={() => handleRadiusChange(50)}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-xl shadow-xs transition-colors cursor-pointer"
              >
                Max 50 km Radius
              </button>
              <button
                type="button"
                onClick={handleClearLocation}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-colors cursor-pointer"
              >
                View All India ({totalWithinIndia})
              </button>
            </div>
          </div>
        )}

      </div>
    </section>
  );
};
